import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Extension } from "@tiptap/react";
import { parseFromMarkdown } from "../../conversion";
import { createNodeFromJson, replaceWithPastedBlocks } from "./markdown-paste-insertion";

const STRUCTURAL_HTML_RE = /<(?:ul|ol|li|blockquote|h[1-6]|pre|table)\b/i;
const MARKDOWN_BLOCK_RE = /(^|\n)\s*(?:#{1,6}\s+\S|[-*+]\s+\S|\d+[.)]\s+\S|>\s+\S|```)/;
const WORD_BULLET_RE = /(^|\n)\s*[•◦▪‣]\s+\S/;
const WORD_BULLET_LINE_RE = /^(\s*)[•◦▪‣]\s+/gm;

export const ExternalMarkdownPaste = Extension.create({
  name: "externalMarkdownPaste",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("externalMarkdownPaste"),
        props: {
          handlePaste(view, event) {
            const text = event.clipboardData?.getData("text/plain") ?? "";
            const html = event.clipboardData?.getData("text/html") ?? "";
            if (!shouldParseAsExternalMarkdown(text, html)) return false;

            const normalizedText = normalizeExternalMarkdownText(text);
            const nodes = parseFromMarkdown(normalizedText)
              .map((node) => createNodeFromJson(view.state.schema, node))
              .filter((node): node is ProseMirrorNode => node !== null);
            if (nodes.length === 0) return false;

            if (!replaceWithPastedBlocks(view, Fragment.fromArray(nodes))) return false;
            event.preventDefault();
            return true;
          },
        },
      }),
    ];
  },
});

function shouldParseAsExternalMarkdown(text: string, html: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;

  // Clean semantic HTML should go through ProseMirror's DOM parser so it can
  // preserve supported rich structure directly from the source.
  if (html && STRUCTURAL_HTML_RE.test(html)) return false;

  return MARKDOWN_BLOCK_RE.test(text) || WORD_BULLET_RE.test(text);
}

function normalizeExternalMarkdownText(text: string): string {
  return text.replace(WORD_BULLET_LINE_RE, "$1- ");
}
